import ArgumentLabel from '@/components/arguments/ArgumentLabel';
import SliderClose from '@/components/arguments/argumentOptionsSlider/SliderClose';
import SliderHeader from '@/components/arguments/argumentOptionsSlider/SliderHeader';
import SliderTitle from '@/components/arguments/argumentOptionsSlider/SliderTitle';
import ArgumentWrapper from '@/components/arguments/ArgumentWrapper';
import Input from '@/components/Input';
import OptionSelector from '@/components/OptionSelector';
import Panel from '@/components/panel/Panel';
import { FiBookmark, HiOutlineDocumentDuplicate, HiOutlineDocumentRemove, HiOutlineDocumentText } from '@/components/react-icons/main';
import Select from '@/components/Select';
import Toggle from '@/components/Toggle';
import { action as reduxAction } from '@/lib/api';
import { DeleteIcon } from '@/lib/constants/icons';
import getValue from '@/lib/getValue';
import { deleteWorkflowMemory, setWorkflowMemory } from '@/redux/actions/gravityActions';
import argumentSelectors from '@/redux/selectors/argumentSelectors';
import selectors from '@/redux/selectors/selectors';
import argumentDataSources from '@/schema/argumentDataSources';
import appActions from '@/screens/apps/redux/actions/appActions';
import stepActions from '@/screens/workflows/redux/actions/stepActions';
import workflowActions from '@/screens/workflows/redux/actions/workflowActions';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import paginationBatchSizeOptions from '@/screens/workflows/schema/paginationBatchSizeOptions';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const baseTypeOptions = [
  { value: 'default', label: 'Single Page', description: 'Get a single page of data.', icon: <HiOutlineDocumentRemove /> },
  { value: 'all', label: 'All Pages', description: 'Get all pages of data.', icon: <HiOutlineDocumentDuplicate /> },
  { value: 'batch', label: 'Batch All Pages', description: 'Get all pages of data in batches.', icon: <HiOutlineDocumentText /> }
];

const maxPagesReachedOptions = [
  { label: 'End Workflow', value: 'end' },
  { label: 'Continue', value: 'continue' },
  { label: 'Do Nothing', value: '' }
];

const PaginationSlider = ({ dataSource, dataSourcePath, dataSavePath, onClose, disabled = false }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const workflowId = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.id);
  const environmentId = useSelector((state) => workflowScreenSelectors.activeWorkflowMetadata(state)?.environmentId);
  const workflowMemory = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.variables?.memory);
  const stepId = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state)?.id);
  const paginationPage = useSelector((state) => selectors.activeWorkflowMemories(state)?.find((n) => n?.memoryKey === `paginationPage_${stepId}`));
  const options = useSelector(argumentSelectors[dataSource].activeOptions);
  const action = useSelector(argumentSelectors[dataSource].activeAction());
  const api = useSelector(argumentSelectors[dataSource].activeAppApi);

  const typeOptions = dataSource === argumentDataSources.appActionInstance ? baseTypeOptions?.filter((n) => n.value !== 'batch') : baseTypeOptions;
  const actions = dataSource === argumentDataSources.workflowStep ? stepActions : appActions;
  const value = options?.pagination || {};
  const type = value?.type;
  const limitDef = api?.features?.limit;
  const offsetDef = api?.features?.offset;
  const limitOnly = action?.features?.limit?.isActive && !action?.features?.offset?.isActive;
  const offsetType = offsetDef?.type;
  const minValue = parseFloat(limitDef?.minValue || 1);
  const maxValue = parseFloat(limitDef?.maxValue || 10000);
  const defaultValue = parseFloat(limitDef?.defaultLimit || 25);
  const initialLimit = value?.limit || defaultValue;
  const limit = initialLimit < minValue ? minValue : initialLimit > maxValue ? maxValue : initialLimit;
  const isCursor = offsetType === 'cursor';
  const isSuboptimalLimit = value?.limit < maxValue * 0.8;
  const batchSizeOptions = useMemo(() => {
    const filtered = paginationBatchSizeOptions.filter((n) => n.value <= maxValue && n.value >= minValue);

    if (!filtered.find((n) => n.value === minValue)) {
      filtered.push({ value: minValue, label: `${minValue} Records` });
    }
    if (!filtered.find((n) => n.value === maxValue)) {
      filtered.push({ value: maxValue, label: `${maxValue} Records` });
    }
    if (!filtered.find((n) => n.value === defaultValue)) {
      filtered.push({ value: defaultValue, label: `${defaultValue} Records` });
    }

    filtered.sort((a, b) => a.value - b.value);

    return filtered;
  }, [minValue, maxValue, defaultValue]);

  const handlePaginationPageChange = async (e) => {
    reduxAction('SET_WORKFLOW_MEMORY', { key: `paginationPage_${stepId}`, value: e.target.value, workflowId });
    await setWorkflowMemory({ key: `paginationPage_${stepId}`, value: e.target.value, workflowId, environmentId });
  };

  const handlePaginationPageDelete = async (value) => {
    if (!value) {
      return;
    }
    const { memoryKey, workflowId } = value;
    const newMemory = { ...workflowMemory };
    delete newMemory[memoryKey];
    await workflowActions.set('variables.memory', newMemory);
    await deleteWorkflowMemory({ key: memoryKey, workflowId, environmentId });
  };

  const handleChange = (val) => {
    actions.set(`${dataSavePath ? `${dataSavePath}.options` : dataSourcePath}.pagination`, (prev) => ({ ...prev, ...val, isActive: true }));
    if (val.type) {
      setSelectedOption(val.type);
    }
  };

  const handlePaginationTypeChange = ({ type }) => {
    handleChange({ type, limit });
  };

  const handleBatchSizeChange = ({ value: val }) => {
    handleChange({ limit: parseFloat(val) });
    if (type === 'batch' && value?.rememberPage) {
      handlePaginationPageChange(1);
    }
  };

  const handleOffsetChange = (e) => {
    if (isNaN(e.target.value) || !e.target.value) {
      return;
    }
    handleChange({ page: Math.max(0, parseFloat(e.target.value - 1)) });
  };

  const handleRememberPageChange = (value) => {
    handleChange({ rememberPage: value });
  };

  const handleResetWhenTotalReachedChange = (value) => {
    handleChange({ resetWhenTotalReached: value });
  };

  const handleEnablePageLimitChange = (value) => {
    handleChange({ enablePageLimit: value });
  };

  const handleMaxPagesChange = (e) => {
    const numValue = parseFloat(e.target.value);
    if (!isNaN(numValue)) {
      handleChange({ maxPages: numValue });
    }
  };

  const handleIfMaxPagesReachedChange = ({ value }) => {
    handleChange({ ifMaxPagesReached: value });
  };

  const handleDelete = () => {
    actions.set(`${dataSavePath ? `${dataSavePath}.options` : dataSourcePath}`, (prev) => ({ ...prev, pagination: null }));
    onClose();
  };

  const handleOptionClick = (option) => {
    if (selectedOption) {
      handlePaginationTypeChange({ type: null });
      setSelectedOption(null);
      return;
    }

    handlePaginationTypeChange({ type: option.value });
  };

  useEffect(() => {
    setSelectedOption(type);
  }, [type]);

  useEffect(() => {
    setTimeout(() => {
      document.querySelector('.pagination-slider .comp-input input')?.focus();
    }, 200);
  }, []);

  return (
    <>
      <SliderHeader showDelete showRun onClose={onClose} onDelete={handleDelete} disabled={disabled} />
      <SliderTitle //
        icon={FiBookmark}
        title="Configure Pagination"
        description="Pagination is how we divide a large set of data into smaller, more manageable sections or pages."
        helpIconContext="workflowStepPaginationContext"
      />
      {!limitOnly && (
        <Panel.Section title="Pagination Type" helpIconContext="workflowStepPaginationContext">
          <div className="pagination-types">
            {(selectedOption ? [typeOptions.find((option) => option.value === selectedOption)].filter(Boolean) : typeOptions).map((option) => (
              <OptionSelector //
                key={option.value}
                isActive={type === option.value}
                isSelected={selectedOption}
                onClick={disabled ? null : () => handleOptionClick(option)}
                icon={option.icon}
                label={option.label}
                description={option.description}
              />
            ))}
          </div>
        </Panel.Section>
      )}
      {(minValue !== maxValue || (value?.type === 'default' && !limitOnly && offsetType !== 'cursor')) && (
        <Panel.Section title="Pagination Settings" helpIconContext="workflowStepPaginationContext">
          {minValue !== maxValue && (
            <ArgumentWrapper>
              <ArgumentLabel className={'small-gap'} label={'Records Per Page'} description={'How many records to return on each fetch?'} helpIcon={'paginationRecords'} />
              <Select //
                disabled={disabled}
                placeholder={value?.limit ? `${value?.limit} Records` : 'Number of Records'}
                value={value?.limit}
                onChange={handleBatchSizeChange}
                options={batchSizeOptions}
                useSearch={false}
              />
            </ArgumentWrapper>
          )}
          {selectedOption === 'all' && isSuboptimalLimit && <Panel.Alert description={`Fetching all records can take a long time. It is recommended you return ${maxValue} records at a time to speed this up.`} />}
          {value?.type === 'default' && !limitOnly && offsetType !== 'cursor' && (
            <ArgumentWrapper>
              <ArgumentLabel className={'small-gap'} label={'Page'} description={'Which page to return?'} />
              <Input //
                disabled={disabled}
                placeholder={'Set a Page Number'}
                value={value?.page + 1 || 1}
                onChange={handleOffsetChange}
                type={'number'}
              />
            </ArgumentWrapper>
          )}
        </Panel.Section>
      )}
      {value?.type === 'batch' && (
        <Panel.Section title="Batch Settings" className="">
          <>
            <ArgumentWrapper>
              <ArgumentLabel className={'small-gap'} label={'Remember Page?'} description={'If enabled, the next workflow run will start from the next page.'}>
                <Toggle //
                  disabled={disabled}
                  size={'sm'}
                  active={value?.rememberPage}
                  onChange={handleRememberPageChange}
                />
              </ArgumentLabel>
            </ArgumentWrapper>
            {value?.rememberPage && (
              <>
                <ArgumentWrapper>
                  <ArgumentLabel className={'small-gap'} label={`Current ${isCursor ? 'Cursor' : 'Page'}`} description={'The current page number.'} />
                  <Input //
                    disabled={disabled}
                    placeholder={isCursor ? 'Cursor' : 'Page Number'}
                    value={paginationPage?.memoryValue || isCursor ? paginationPage?.memoryValue : 1}
                    onChange={handlePaginationPageChange}
                    icon={DeleteIcon}
                    iconPosition={'right'}
                    iconClassName={'light'}
                    onIconClick={() => handlePaginationPageDelete(paginationPage)}
                  />
                </ArgumentWrapper>
                <ArgumentWrapper>
                  <ArgumentLabel className={'small-gap'} label={'Reset When Total Reached?'} description={'If enabled, the page number will reset when the total number of records is reached.'}>
                    <Toggle //
                      disabled={disabled}
                      size={'sm'}
                      active={value?.resetWhenTotalReached ?? true}
                      onChange={handleResetWhenTotalReachedChange}
                    />
                  </ArgumentLabel>
                </ArgumentWrapper>
                <ArgumentWrapper>
                  <ArgumentLabel className={'small-gap'} label={'Enable Page Limit?'} description={'If enabled, the workflow will stop after this limit is reached.'}>
                    <Toggle //
                      disabled={disabled}
                      size={'sm'}
                      active={value?.enablePageLimit}
                      onChange={handleEnablePageLimitChange}
                    />
                  </ArgumentLabel>
                </ArgumentWrapper>
                {value?.enablePageLimit && (
                  <>
                    <ArgumentWrapper>
                      <ArgumentLabel className={'small-gap'} label={'Max Pages Per Run'} description={'The maximum number of pages to run.'} />
                      <Input //
                        disabled={disabled}
                        placeholder={'Max Pages Per Run'}
                        value={value?.maxPages || ''}
                        onChange={handleMaxPagesChange}
                        type={'number'}
                      />
                    </ArgumentWrapper>
                    <ArgumentWrapper>
                      <ArgumentLabel className={'small-gap'} label={'If Max Pages Reached?'} description={'What to do if the max pages are reached.'} />
                      <Select //
                        disabled={disabled}
                        placeholder={'If Max Pages Reached?'}
                        value={getValue(maxPagesReachedOptions, value?.ifMaxPagesReached || '')}
                        onChange={handleIfMaxPagesReachedChange}
                        options={maxPagesReachedOptions}
                        nullable
                      />
                    </ArgumentWrapper>
                  </>
                )}
              </>
            )}
          </>
        </Panel.Section>
      )}
      <SliderClose onClose={onClose} />
    </>
  );
};

export default PaginationSlider;
